import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { SERVICE, HOME } from "../constants/testIds";
import {
  Activity, Stethoscope, BedDouble, ScanLine, Scan, Radiation, Pill, TestTube,
  ShieldCheck, Clock, Users, ArrowRight, Star, CheckCircle2
} from "lucide-react";

const services = [
  { key: "ultrasound", to: "/book/ultrasound", icon: Activity, tint: "tint-ultrasound", accent: "text-blue-600", bg: "bg-blue-50" },
  { key: "opd",        to: "/book/opd",        icon: Stethoscope, tint: "tint-opd", accent: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "pathology",  to: "/pathology",       icon: TestTube, tint: "tint-pathology", accent: "text-fuchsia-600", bg: "bg-fuchsia-50" },
  { key: "ipd",        to: "/book/ipd",        icon: BedDouble, tint: "tint-ipd", accent: "text-violet-600", bg: "bg-violet-50" },
  { key: "xray",       to: "/book/xray",       icon: ScanLine, tint: "tint-xray", accent: "text-sky-600", bg: "bg-sky-50" },
  { key: "ct",         to: "/book/ct",         icon: Scan, tint: "tint-ct", accent: "text-orange-600", bg: "bg-orange-50" },
  { key: "mri",        to: "/book/mri",        icon: Radiation, tint: "tint-mri", accent: "text-pink-600", bg: "bg-pink-50" },
  { key: "pharmacy",   to: "/pharmacy",        icon: Pill, tint: "tint-pharmacy", accent: "text-amber-600", bg: "bg-amber-50" },
];

export default function Home() {
  const { lang } = useApp();
  return (
    <div className={`App ${lang === "hi" ? "lang-hi" : ""}`}>
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-emerald-50 -z-10" />
        <div className="absolute inset-0 grain opacity-40 -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="chip bg-blue-100 text-blue-700">
              <ShieldCheck size={14}/> {t(lang, "hero.badge")}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-5 leading-[1.05]">
              {t(lang, "hero.title")}
            </h1>
            <p className="text-lg text-slate-600 mt-5 max-w-2xl leading-relaxed">
              {t(lang, "hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/book/opd" data-testid={HOME.bookNowHero} className="btn-primary">
                {t(lang, "hero.cta1")} <ArrowRight size={18}/>
              </Link>
              <Link to="/pharmacy" data-testid={HOME.browsePharmacy} className="btn-outline">
                {t(lang, "hero.cta2")}
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Users size={18} className="text-blue-600"/>{t(lang, "hero.stat1")}</div>
              <div className="flex items-center gap-2"><Stethoscope size={18} className="text-emerald-600"/>{t(lang, "hero.stat2")}</div>
              <div className="flex items-center gap-2"><Clock size={18} className="text-orange-600"/>{t(lang, "hero.stat3")}</div>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: ".15s" }}>
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-blue-200/50 to-emerald-200/50 rounded-[2rem] blur-2xl -z-10"/>
              <img
                src="https://images.unsplash.com/photo-1673865641073-4479f93a7776?crop=entropy&cs=srgb&fm=jpg&w=900&q=85"
                alt="Doctor"
                className="rounded-[2rem] w-full object-cover aspect-[4/5] card-shadow"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl card-shadow p-4 flex items-center gap-3 max-w-[220px]">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <CheckCircle2 size={20}/>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Booking Confirmed</div>
                  <div className="text-sm font-semibold">Dr. Ananya · 10:30 AM</div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl card-shadow px-4 py-3">
                <div className="flex items-center gap-1 text-amber-500">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor"/>)}
                </div>
                <div className="text-xs text-slate-500 mt-1">Trusted 4.8/5</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="chip bg-emerald-100 text-emerald-700">Services</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3">{t(lang, "services.title")}</h2>
            <p className="text-slate-600 mt-2">{t(lang, "services.sub")}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map(({ key, to, icon: Icon, accent, bg }) => (
            <Link
              key={key} to={to}
              data-testid={SERVICE.card(key)}
              className={`group rounded-3xl bg-white border border-slate-100 p-6 card-shadow card-shadow-hover flex flex-col gap-4`}
              style={{ transition: "transform .25s, box-shadow .25s" }}
            >
              <div className={`w-14 h-14 rounded-2xl ${bg} ${accent} flex items-center justify-center`}>
                <Icon size={28} strokeWidth={1.8}/>
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold">{t(lang, `services.${key}.name`)}</h3>
                <p className="text-sm text-slate-600 mt-1">{t(lang, `services.${key}.desc`)}</p>
              </div>
              <div className="text-xs font-medium text-slate-500 mt-auto">{t(lang, `services.${key}.fee`)}</div>
              <div className={`mt-1 inline-flex items-center gap-1 text-sm ${accent} font-medium`}>
                {t(lang, "nav.bookNow")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1"/>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white border-y border-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: lang === "hi" ? "सुरक्षित भुगतान" : "Secure Payments", desc: lang === "hi" ? "UPI और कार्ड से" : "UPI & cards, encrypted end-to-end" },
            { icon: Clock, title: lang === "hi" ? "24×7 सपोर्ट" : "24×7 Support", desc: lang === "hi" ? "WhatsApp और फ़ोन पर" : "WhatsApp and phone anytime" },
            { icon: Stethoscope, title: lang === "hi" ? "200+ विशेषज्ञ" : "200+ Specialists", desc: lang === "hi" ? "अनुभवी डॉक्टर, बेगूसराय" : "Board-certified doctors in Begusarai" },
          ].map((f, i) => (
            <div key={i} className="rounded-2xl bg-slate-50 p-6 flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-blue-600 card-shadow">
                <f.icon size={24}/>
              </div>
              <div>
                <h4 className="font-heading font-semibold">{f.title}</h4>
                <p className="text-sm text-slate-600 mt-1">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-heading text-3xl font-bold text-center">FAQ</h2>
        <div className="mt-8 space-y-3">
          {[
            { q: lang === "hi" ? "बुकिंग कैसे रद्द करें?" : "How can I cancel a booking?", a: lang === "hi" ? "डैशबोर्ड से आप 24 घंटे पहले तक बुकिंग रद्द कर सकते हैं।" : "You can cancel from your dashboard up to 24 hours in advance." },
            { q: lang === "hi" ? "क्या रिपोर्ट ऑनलाइन मिलेगी?" : "Will reports be available online?", a: lang === "hi" ? "हाँ, रिपोर्ट आपके डैशबोर्ड पर डाउनलोड के लिए उपलब्ध होगी।" : "Yes, reports are available for download from your dashboard." },
            { q: lang === "hi" ? "भुगतान कैसे करें?" : "How do I pay?", a: lang === "hi" ? "आप UPI (9798203741) या कार्ड से भुगतान कर सकते हैं।" : "You can pay via UPI (9798203741) or card." },
          ].map((f, i) => (
            <details key={i} className="group bg-white rounded-2xl border border-slate-100 p-5 card-shadow">
              <summary className="font-medium cursor-pointer flex items-center justify-between">
                {f.q}
                <span className="text-blue-600 group-open:rotate-45 transition-transform">＋</span>
              </summary>
              <p className="text-slate-600 mt-3 text-sm">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <WhatsAppFab />
      <Footer />
    </div>
  );
}
